import { getValueUnitDivisor } from "../utilities/UnitsAndKeys/convertUnit";
import { getDataKeyName } from "../utilities/UnitsAndKeys/getDataKeyName";
import { getUnitColor } from "../utilities/UnitsAndKeys/getUnitColor";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import React, { useEffect, useRef, useCallback } from "react";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import { formatData } from "./Graph.utils";
import { endOfMonth } from "date-fns";
import {
  styleValueAxis,
  styleDateAxis,
  styleLegend,
} from "../utilities/Charts/styles";

am4core.useTheme(am4themes_animated);

export const Graph = ({
  data,
  totals,
  graphId,
  aggregation = null,
  dataKeys,
  hideScroll,
  hideLegend,
  height = 500,
  selectionCallback,
  keyOptions,
  forceKw,
}) => {
  const chart = useRef(null);

  useEffect(() => {
    chart.current = am4core.create(`chart${graphId}`, am4charts.XYChart);
    return () => chart.current.dispose();
  }, [graphId]);

  const getKeyName = useCallback((dataKey) => {
    return getDataKeyName(dataKey)[0];
  }, []);

  useEffect(() => {
    if (chart.current) {
      chart.current.dispose();
      chart.current = am4core.create(`chart${graphId}`, am4charts.XYChart);

      // system data doesnt show aggregation, have to assume it from the length of the different formatted date string
      const isBar = aggregation
        ? aggregation === "day" ||
          aggregation === "month" ||
          aggregation === "30 minute"
        : data && data[0].time.length < 11;
      const isClickable = aggregation === "day" || aggregation === "month";

      chart.current.paddingRight = 25;
      const zoomButtonBG = chart.current.zoomOutButton.background;
      zoomButtonBG.fill = am4core.color("#808080");
      if (zoomButtonBG.states.getKey("hover").properties.fill) {
        zoomButtonBG.states.getKey("hover").properties.fill = am4core.color(
          "#5A5A5A"
        );
      }
      if (zoomButtonBG.states.getKey("down").properties.fill) {
        zoomButtonBG.states.getKey("down").properties.fill = am4core.color(
          "#808080"
        );
      }

      if (data) {
        let pivotSum = 0;
        let typeCount = 0;
        if (totals) {
          dataKeys.forEach((dataKey) => {
            pivotSum += totals[dataKey];
            typeCount++;
          });
        }

        // dumb heuristic but it works
        const pivot =
          dataKeys.length > 2
            ? typeCount
              ? pivotSum / data.length / (typeCount / 2)
              : data[Math.floor(data.length - data.length / 4)][
                  dataKeys[dataKeys.length - 1]
                ]
            : 1000;

        const { unit, divisor } = getValueUnitDivisor(
          pivot,
          forceKw ? "power" : "energy"
        );

        // format the data
        const values = formatData(
          data,
          keyOptions,
          dataKeys,
          divisor,
          isClickable
        );

        // add padding if theres weather icons
        if (values[0] && values[0].weatherIcon) {
          chart.current.paddingTop = values[0].weatherIcon ? 40 : 20;
        }

        chart.current.data = data && values;
        chart.current.numberFormatter.numberFormat = "#.##s";

        // Axes =====
        let dateAxis = chart.current.xAxes.push(new am4charts.DateAxis());
        styleDateAxis(dateAxis);

        let valueAxis = chart.current.yAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = `[bold]${
          forceKw ? "Power" : "Energy"
        } (${unit})[/]`;
        styleValueAxis(valueAxis);

        // Weather icons
        var iconAxis = chart.current.xAxes.push(new am4charts.CategoryAxis());
        iconAxis.dataFields.category = "weatherIcon";
        iconAxis.renderer.grid.template.strokeOpacity = 0;
        iconAxis.renderer.opposite = true;
        iconAxis.renderer.minGridDistance = 50;
        iconAxis.renderer.labels.template.disabled = true;
        if (iconAxis.tooltip) {
          iconAxis.tooltip.disabled = true;
        }
        var image = new am4core.Image();
        image.width = 50;
        image.height = 50;
        image.dy = -25;
        image.horizontalCenter = "middle";
        image.verticalCenter = "middle";
        iconAxis.dataItems.template.bullet = image;
        image.adapter.add("href", (href, target) => {
          const dataItem = target.dataItem;
          if (dataItem && dataItem.category) {
            const weatherString = dataItem.category.replace(/ \(.*/, "");
            return `/icons/Weather/${weatherString}.svg`;
          }
        });

        // Scroll =====
        let scrollbarX;
        if (!hideScroll) {
          scrollbarX = new am4charts.XYChartScrollbar();
          chart.current.scrollbarX = scrollbarX;
        }

        // Series =====
        dataKeys.forEach((dataKey, index) => {
          if (chart.current) {
            const opts = keyOptions[dataKey];
            const series = chart.current.series.push(
              isBar && !opts.forceLine
                ? new am4charts.ColumnSeries()
                : new am4charts.LineSeries()
            );

            series.dataFields.dateX = "time";
            series.dataFields.categoryX = "weatherIcon";
            series.name = opts.name || getKeyName(dataKey);
            series.dataFields.valueY = dataKey;
            series.fillOpacity = opts.opacity || 0.75;
            series.fill = am4core.color(
              opts.color || getUnitColor(dataKey, index)
            );
            series.stroke = am4core.color(
              opts.color || getUnitColor(dataKey, index)
            );

            if (!opts.filled) {
              series.strokeWidth = 1;
              series.fillOpacity = 0;
            }

            series.tooltipHTML = `${opts.name ||
              getKeyName(dataKey)}: <strong>{valueY.value} ${unit}</strong>`;
            series.tooltip.label.adapter.add(
              "htmlOutput",
              (text, target, key) => {
                // if (target.dataItem) {
                //   if (target.dataItem.values.valueY.value === 0) {
                //     return "";
                //   }
                // }
                return text;
              }
            );

            // if column charts
            if (isBar && !opts.forceLine) {
              series.stacked = opts.stacked;
              series.zIndex = opts.barZIndex || 1;

              // click to go to day
              if (isClickable && selectionCallback) {
                series.cursorOverStyle = am4core.MouseCursorStyle.pointer;
                series.columns.template.events.on("hit", (ev) => {
                  const startDate = ev.target.dataItem.dateX;
                  const endDate =
                    aggregation === "day"
                      ? ev.target.dataItem.dateX
                      : endOfMonth(ev.target.dataItem.dateX);
                  selectionCallback("CUSTOM", startDate, endDate);
                });
              }
            } else if (isBar && opts.forceLine) {
              series.zIndex = 4;
              series.strokeWidth = 1;
              series.strokeOpacity = 1;
            } else {
              series.zIndex = opts.zIndex || 1;
            }
            if (!hideScroll) {
              scrollbarX.series.push(series);
            }
          }
        });

        // Legends
        if (!hideLegend) {
          chart.current.legend = new am4charts.Legend();
          styleLegend(chart.current.legend);
        }

        chart.current.cursor = new am4charts.XYCursor();
      }
    }
  }, [
    data,
    getKeyName,
    graphId,
    keyOptions,
    totals,
    hideScroll,
    hideLegend,
    dataKeys,
    aggregation,
    forceKw,
  ]);

  return <div style={{ height: height }} id={`chart${graphId}`} />;
};

export default Graph;



// WEBPACK FOOTER //
// ./resources/assets/js/app/Graph/Graph.js